import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";


import { notify } from "@cayuse/shared-react.components.banner-notification";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { isFunction, isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { api } from "services";

import ConfirmationModal from "components/ConfirmationModal";
import Dropdown from "components/Dropdown";
import SubformNotification from "containers/Form/Common/SubformNotification";
import { COLUMN_HEADER_KEYS, FORMS_BASE_ROUTE } from "globals/constants";
import { useDictionary, useFormBuilderType } from "hooks";
import { getForm } from "store/actions/form";
import { formSelectors } from "store/selectors";
import { findElementAndSectionWithType, getRulesWithUnknownTeams, notifyError, spUrl } from "utils";
import { decodeFormModelIds, getFormType, getInvalidFormElements, getUnmatchedRules, validateFormData } from "utils/forms";

import { InvalidElementsError } from "./components/InvalidElementsError";
import { UnknownTeamsError } from "./components/UnknownTeamsError";
import { UnmatchedRulesError } from "./components/UnmatchedRulesError";
import styles from "./ModelerToolbar.css";


function ModelerToolbar ({
	formId,
	history,
	isSaveBtnDisabled,
	getInProgressDraft,
	modelKey,
	renderPreview,
	saveDraft,
	setIsSaveBtnDisabled,
	setShouldUpdateModelJson,
}) {
	const dictionary = useDictionary("formDesigner");
	const [ showPublishConfirmation, setShowPublishConfirmation ] = useState(false);
	const [ areDraftNotificationsEnabled, setAreDraftNotificationsEnabled ] = useState(false);
	const { rules, formElements } = useSelector(formSelectors.content);
	const isSimpleFormBuilder = useFormBuilderType() === "simple";

	const disableFormRules = useMemo(() => {
		const type = getFormType(modelKey);

		return type === COLUMN_HEADER_KEYS.SUBAWARD || type === COLUMN_HEADER_KEYS.AGREEMENT;
	}, [ modelKey ]);

	const trimFormId = useCallback(formId => {
		const firstDashIndex = formId.indexOf("-");

		return firstDashIndex > -1
			? formId.substr(firstDashIndex + 1)
			: formId;
	}, []);

	const handleShowPublishConfirmationModal = useCallback(() => {
		if (isNonEmptyString(formId)) {
			getForm(trimFormId(formId));
		}
		setShowPublishConfirmation(prev => !prev);
	}, [ formId, trimFormId ]);

	const confirmPublish = async () => {
		const unmatchedRulesData = await getUnmatchedRules(rules, formElements, modelKey);
		const rulesWithUnknownTeams = await getRulesWithUnknownTeams(rules);
		const model = await api.form.getSimplifiedModel(formId);
		const element = findElementAndSectionWithType(model, "subform");
		const invalidElements = validateFormData(model, dictionary.notifications.noValue);

		if (invalidElements.length > 0) {
			notify(<InvalidElementsError invalidElements={invalidElements} />);

			return;
		}

		if (!isSimpleFormBuilder && element) {
			notify(
				<SubformNotification elementLabel={element.elementLabel} sectionLabel={element.sectionLabel}/>,
				{ type: "error", autoClose: false }
			);
		} else {
			const modelJson = await getInProgressDraft();
			const invalidElements = getInvalidFormElements(formElements, modelJson, isSimpleFormBuilder);

			if (isNonEmptyArray(invalidElements)) {
				notify(<InvalidElementsError invalidElements={invalidElements} />);
			}
			else if (isNonEmptyArray(unmatchedRulesData.rulesWithoutMatch)) {
				notify(<UnmatchedRulesError unmatchedRulesData={unmatchedRulesData} modelKey={modelKey} rules={rules} />);
			}
			else if (isNonEmptyArray(rulesWithUnknownTeams)) {
				notify(<UnknownTeamsError rulesWithUnknownTeams={rulesWithUnknownTeams} />);
			}
			else {
				try {
					const modelJson = await api.form.getSimplifiedModel(formId);

					await api.form.updateSimplifiedModel(formId, decodeFormModelIds(modelJson));

					const appModelList = await api.form.getModelList(formId);

					const appModel = appModelList.data.find(app =>
						isNonEmptyArray(app.appDefinition.formModels) && app.appDefinition.formModels.find(form => form.id === formId)
					);
					const appModelId = appModel.id;
					await api.form.publish(appModelId, { comment: "" });

					const form = appModel.appDefinition.formModels.find(form => form.id === formId);
					await api.form.saveTemplate(form.key);

					notify(dictionary.notifications.publishSuccess || "The form was published", { type: "success" });

					if (isSimpleFormBuilder && isSaveBtnDisabled) {
						setShouldUpdateModelJson(true);
					}
				} catch (error) {
					notifyError(dictionary.notifications.publishFail || "The form was not published", error);
				}
			}

			setShowPublishConfirmation(false);
		}
	};

	const defineSaveBtnObserver = () => {
		let shouldDisableSaveBtn = false;

		return new MutationObserver(mutations => {
			for (let mutation of mutations) {
				if (mutation.attributeName === "disabled") {
					for (let attribute of mutation.target.attributes) {
						if (attribute.name === "disabled") {
							shouldDisableSaveBtn = true;
							break;
						} else {
							shouldDisableSaveBtn = false;
						}
					}
					setIsSaveBtnDisabled(shouldDisableSaveBtn);
					break;
				}
			}
		});
	};

	// Interval to detect when form builder is loaded
	const checkForFormChanges = setInterval(() => {
		const flowableFormSaveBtn = document.getElementsByClassName("eo-vis-menu-save-all");
		let saveBtnObserver;

		if (flowableFormSaveBtn.length) {
			const isFlowableFormSaveBtnDisabled = flowableFormSaveBtn[0].classList.contains("disabled");

			// Form builder is loaded so we stop the interval
			clearInterval(checkForFormChanges);

			if (isFlowableFormSaveBtnDisabled && !saveBtnObserver) {
				// Observer to detect when save button should be enabled/disabled
				saveBtnObserver = defineSaveBtnObserver();
				saveBtnObserver.observe(flowableFormSaveBtn[0], { attributes: true });
			}
			// ID of the currently rendered tabs container from the DOM to validate against unsaved form state
			const tabsContainerId = document.querySelector("g#svg-panel1 g.children[style*=\"overflow:hidden\"] > g")?.id;

			if (!isSaveBtnDisabled && !document.getElementById(tabsContainerId)) {
				setIsSaveBtnDisabled(true);
			}
		}
	}, 500);

	const handleSaveDraft = useCallback(() => {
		if (!areDraftNotificationsEnabled) {
			setAreDraftNotificationsEnabled(true);
		}

		if (isFunction(saveDraft)) {
			saveDraft();
		}
	}, [ areDraftNotificationsEnabled, saveDraft ]);

	const onSaveDraft = async (formId, areDraftNotificationsEnabled) => {
		try {
			const response = await api.form.saveDraft(formId);

			if (areDraftNotificationsEnabled) {
				if (response === true) {
					notify(dictionary.notifications.draftSuccess || "The draft was saved", { type: "success" });
				} else {
					notifyError(dictionary.notifications.draftFail || "The draft could not be saved");
				}
			}
		} catch (error) {
			if (areDraftNotificationsEnabled) {
				notify(dictionary.notifications.draftFail || "The draft could not be saved");
			}
		}
	};

	useEffect(() => {
		if (isSaveBtnDisabled === true) {
			onSaveDraft(formId, areDraftNotificationsEnabled);
		}
	}, [ formId, isSaveBtnDisabled, areDraftNotificationsEnabled ]);

	return (
		<>
			<ConfirmationModal
				icon={"file-alt"}
				message={dictionary?.publishConfirmation?.description || "Please confirm that you want to publish all your forms for your Sponsored Project."}
				title={dictionary?.publishConfirmation?.title || "Would you really like to publish all forms?"}
				visible={showPublishConfirmation}
				onConfirm={confirmPublish}
				onCancel={() => setShowPublishConfirmation(false)}
			/>
			<Dropdown
				aria-label="Manage Form"
				className={styles.dropdown}
				openOnFocus={false}
				selectOnNavigation={false}
				selectOnBlur={false}
				closeOnChange={true}
				options={
					!disableFormRules
						? [
							{ key: "rules", value: "rules", icon: "book", text: dictionary?.manageFormRules || "Form Rules", className: styles.dropdownMenuItem },
							{ key: "history", value: "history", icon: "undo", text: dictionary?.manageFormRevisions || "Revisions", className: styles.dropdownMenuItem }
						]
						: [
							{ key: "history", value: "history", icon: "undo", text: dictionary?.manageFormRevisions || "Revisions", className: styles.dropdownMenuItem }
						]
				}
				onChange={(e, { value }) => {
					history.push(spUrl(`${FORMS_BASE_ROUTE}/${formId}/${value}`));
				}}
			>
			</Dropdown>
			<IconButton
				icon="eye"
				onClick={() => history.push(spUrl(`${FORMS_BASE_ROUTE}/${formId}/preview`))} 
				primary
				className={styles.marginLeft}
			>
				{dictionary?.preview || "Preview"}
			</IconButton>
			<IconButton
				icon="save"
				onClick={handleSaveDraft}
				primary
				className={styles.marginLeft}
				disabled={isSaveBtnDisabled}
			>
				{dictionary?.saveDraft || "Save Draft"}
			</IconButton>
			<IconButton
				icon="file-alt"
				onClick={handleShowPublishConfirmationModal}
				primary
				className={styles.marginLeft}
			>
				{dictionary?.publish || "Publish"}
			</IconButton>
		</>
	);
}

export default ModelerToolbar;
